import { CodeExtension, CodeNode } from "@lexical/code-core";
import { HorizontalRuleExtension, HorizontalRuleNode } from "@lexical/extension";
import { ListExtension, ListItemNode, ListNode } from "@lexical/list";
import { HeadingNode, QuoteNode, RichTextExtension } from "@lexical/rich-text";
import { mergeRegister } from "@lexical/utils";
import {
  $getEditor,
  $getNodeByKey,
  $isElementNode,
  $setState,
  defineExtension,
  ParagraphNode,
  safeCast,
  type ElementNode,
  type LexicalEditor,
  type LexicalExtension,
  type LexicalNode,
  type NodeKey,
} from "lexical";
import { generateBlockId, type BlockIdGenerator } from "../../core/block-id";
import type { BlockId } from "../../core/types";
import { $forEachBlock } from "./operations";
import { $getBlockId, $isIdentified, blockIdState } from "./state";

export interface BlockIdConfig {
  /** Replaced in tests to make IDs reproducible. */
  generate: BlockIdGenerator;
}

/**
 * Holds blocks rather than text: a list, or the item that exists only to wrap a nested list. An
 * ordinary list item is a block in its own right.
 */
const $isContainer = (node: LexicalNode): node is ElementNode => {
  if (!$isElementNode(node)) return false;
  if (node.getType() === "list" || node.getType() === "table") return true;
  return node.getType() === "listitem" && node.getFirstChild()?.getType() === "list";
};

/**
 * What one update has decided so far. Transforms re-run on every node they touch, so without this
 * a node would be re-examined after its own assignment. Dropped when the update commits.
 */
interface Cycle {
  assigned: Set<NodeKey>;
  claimed: Set<BlockId>;
  /** Built at most once per update, and only when a node arrives already carrying an ID. */
  owners: Map<BlockId, NodeKey[]> | null;
}

const cycles = new WeakMap<LexicalEditor, Cycle>();
const generators = new WeakMap<LexicalEditor, BlockIdGenerator>();

/**
 * Identifies a block the caller has just created as a new one, when the rules below would
 * otherwise see it as the continuation of a block it replaced (the composer's cleared draft).
 */
export function $assignFreshBlockId(node: LexicalNode): void {
  const editor = $getEditor();
  $assign(cycleFor(editor), node, (generators.get(editor) ?? generateBlockId)());
}

/**
 * Identifies a block the caller knows to be another block, continued, where the rules below cannot
 * see it (a paste at the very start of a block). Settled for the rest of the update: the transform
 * leaves the node alone, and no other new block can inherit the same ID.
 */
export function $continueBlockId(node: LexicalNode, id: BlockId): void {
  $assign(cycleFor($getEditor()), node, id);
}

function cycleFor(editor: LexicalEditor): Cycle {
  let cycle = cycles.get(editor);
  if (cycle === undefined) {
    cycle = { assigned: new Set(), claimed: new Set(), owners: null };
    cycles.set(editor, cycle);
  }
  return cycle;
}

/** Whether an attached node other than `node` answers to `id` right now. */
function $isTaken(cycle: Cycle, id: BlockId, node: LexicalNode): boolean {
  if (cycle.owners === null) {
    const owners = new Map<BlockId, NodeKey[]>();
    $forEachBlock((block, blockId) => {
      if (blockId === null) return;
      const keys = owners.get(blockId);
      if (keys === undefined) owners.set(blockId, [block.getKey()]);
      else keys.push(block.getKey());
    });
    cycle.owners = owners;
  }
  return (cycle.owners.get(id) ?? []).some((key) => {
    if (key === node.getKey()) return false;
    const other = $getNodeByKey(key);
    return other !== null && other.isAttached() && $getBlockId(other) === id;
  });
}

function $assign(cycle: Cycle, node: LexicalNode, id: BlockId): void {
  $setState(node, blockIdState, id);
  cycle.assigned.add(node.getKey());
  cycle.claimed.add(id);
  const keys = cycle.owners?.get(id);
  if (keys !== undefined) keys.push(node.getKey());
  else cycle.owners?.set(id, [node.getKey()]);
}

/** A block in the last committed state that a new block might be the continuation of. */
interface Donor {
  key: NodeKey;
  id: BlockId;
  /** Its first child then, which is what says where its content went. */
  firstChildKey: NodeKey | null;
}

const $describe = (node: LexicalNode | null): Donor | null => {
  if (node === null || !$isIdentified(node)) return null;
  const id = $getBlockId(node);
  if (id === null) return null;
  const first = $isElementNode(node) ? node.getFirstChild() : null;
  return { key: node.getKey(), id, firstChildKey: first === null ? null : first.getKey() };
};

/** Reads the last committed state from inside an update, without touching node internals. */
const readCommitted = <T>(editor: LexicalEditor, read: () => T): T =>
  editor.getEditorState().read(read, { editor });

/**
 * The content rule: the block that holds the *head* of another block's content is that block,
 * continued. Covers every `replace(node, true)` upstream performs (Markdown shortcuts,
 * `$setBlocksType`, `$insertList`, list-type changes, the heading that turns into a paragraph at
 * offset 0) without hooking any of them.
 */
function $donorByContent(editor: LexicalEditor, node: LexicalNode): Donor | null {
  const first = $isElementNode(node) ? node.getFirstChild() : null;
  if (first === null) return null;
  const donor = readCommitted(editor, () => {
    const before = $getNodeByKey(first.getKey());
    const parent = before === null ? null : before.getParent();
    if (parent === null || parent.getKey() === node.getKey()) return null;
    return $describe(parent);
  });
  return donor !== null && donor.firstChildKey === first.getKey() ? donor : null;
}

/**
 * The position rule, for blocks whose content says nothing (they are empty, or their text is
 * freshly split): a block put exactly where another one was removed continues it, provided that
 * block's content did not move somewhere else. Walks up through new ancestors on this side and
 * down through containers on the other, so "empty paragraph ↔ empty list item" is one block too.
 */
function $donorByPosition(editor: LexicalEditor, node: LexicalNode): Donor | null {
  if ($isContainer(node)) return null;
  let anchor: LexicalNode = node;
  const isNew = (candidate: LexicalNode): boolean =>
    readCommitted(editor, () => $getNodeByKey(candidate.getKey()) === null);
  for (;;) {
    const parent: ElementNode | null = anchor.getParent();
    if (parent === null) return null;
    if (!isNew(parent)) break;
    if (anchor.getPreviousSibling() !== null) return null;
    anchor = parent;
  }
  const parentKey = anchor.getParent()!.getKey();
  const before = anchor.getPreviousSibling();
  const donor = readCommitted(editor, () => {
    let occupant: LexicalNode | null;
    if (before === null) {
      const parent = $getNodeByKey(parentKey);
      occupant = $isElementNode(parent) ? parent.getFirstChild() : null;
    } else {
      const previous = $getNodeByKey(before.getKey());
      occupant = previous === null ? null : previous.getNextSibling();
    }
    while (occupant !== null && $isContainer(occupant)) occupant = occupant.getFirstChild();
    return $describe(occupant);
  });
  if (donor === null) return null;
  // Only a block that is gone can be continued by position. One that is still here keeps its ID,
  // however empty it is: a new neighbour has no claim on it.
  const current = $getNodeByKey(donor.key);
  if (current !== null && current.isAttached()) return null;
  const head = donor.firstChildKey === null ? null : $getNodeByKey(donor.firstChildKey);
  // Its content lives on in another block, which is the one that inherits.
  return head !== null && head.isAttached() ? null : donor;
}

function $identify(node: LexicalNode, generate: BlockIdGenerator): void {
  const editor = $getEditor();
  const cycle = cycleFor(editor);
  const key = node.getKey();
  if (cycle.assigned.has(key) || !node.isAttached()) return;

  const id = $getBlockId(node);
  if (id !== null) {
    const committed = readCommitted(editor, () => {
      const before = $getNodeByKey(key);
      return before === null ? null : $getBlockId(before);
    });
    // The established owner, or a newcomer (pasted, imported) whose ID nobody here uses. Either
    // way the ID is spoken for: a block removed in this same update may have had it (cut and paste,
    // a text drag), and a new block standing where that one stood must not inherit it as well.
    if (committed === id || !$isTaken(cycle, id, node)) return void cycle.claimed.add(id);
  }

  const donor = $donorByContent(editor, node) ?? $donorByPosition(editor, node);
  if (donor === null || cycle.claimed.has(donor.id)) return $assign(cycle, node, generate());
  const current = $getNodeByKey(donor.key);
  if (current !== null && current.isAttached()) {
    // Still here. Only an emptied block gives up its ID: Enter at the start of a block moves the
    // content down, and the block that keeps the content keeps the ID.
    const emptied =
      $isElementNode(current) && current.isEmpty() && $getBlockId(current) === donor.id;
    if (!emptied) return $assign(cycle, node, generate());
    $assign(cycle, current, generate());
    // `claimed` now holds the fresh ID; the donor's is about to be claimed by `node`.
  }
  $assign(cycle, node, donor.id);
}

/**
 * Identifies nodes of a class that is not part of every editor (a table's, which loads on demand)
 * by the same rules as the rest. The type must be in `IDENTIFIED_NODE_TYPES`.
 */
export function registerBlockIdentity(
  editor: LexicalEditor,
  klass: Parameters<LexicalEditor["registerNodeTransform"]>[0],
): () => void {
  return editor.registerNodeTransform(klass, (node: LexicalNode) =>
    $identify(node, generators.get(editor) ?? generateBlockId),
  );
}

/**
 * Keeps every block and list item identified, uniquely, inside the update that created or changed
 * it, so the ID is part of the same undo step. Nothing here runs while typing: Lexical only
 * transforms elements that were changed themselves, not the ancestors of edited text.
 */
export const BlockIdExtension: LexicalExtension<
  BlockIdConfig,
  "z-editor/block-id",
  unknown,
  unknown
> = defineExtension({
  name: "z-editor/block-id",
  config: safeCast<BlockIdConfig>({ generate: generateBlockId }),
  dependencies: [RichTextExtension, ListExtension, CodeExtension, HorizontalRuleExtension],
  register(editor, config) {
    generators.set(editor, config.generate);
    const $transform = (node: LexicalNode): void => $identify(node, config.generate);
    return mergeRegister(
      editor.registerNodeTransform(ParagraphNode, $transform),
      editor.registerNodeTransform(HeadingNode, $transform),
      editor.registerNodeTransform(QuoteNode, $transform),
      editor.registerNodeTransform(CodeNode, $transform),
      editor.registerNodeTransform(ListNode, $transform),
      editor.registerNodeTransform(ListItemNode, $transform),
      editor.registerNodeTransform(HorizontalRuleNode, $transform),
      editor.registerUpdateListener(() => void cycles.delete(editor)),
    );
  },
});
